import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  User,
  Building2,
  MapPin,
  Landmark,
  Save,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  
  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    entity_name: '',
    entity_type: 'individual',
    registration_number: '',
    tax_number: '',
    physical_address: '',
    postal_address: ''
  });
  
  const [bankingDetails, setBankingDetails] = useState({
    bank_name: '',
    bank_branch_code: '',
    bank_account_number: '',
    bank_account_type: ''
  });
  
  const [showBankingDialog, setShowBankingDialog] = useState(false);
  const [bankingPassword, setBankingPassword] = useState('');
  const [showFullBanking, setShowFullBanking] = useState(false);
  const [editingBanking, setEditingBanking] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/profile/full`, { headers });
      setProfileData(response.data);
      
      // Populate form fields
      const { user, profile } = response.data;
      setPersonalInfo({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        phone: user?.phone || '',
        entity_name: profile?.entity_name || '',
        entity_type: profile?.entity_type || 'individual',
        registration_number: profile?.registration_number || '',
        tax_number: profile?.tax_number || '',
        physical_address: profile?.physical_address || '',
        postal_address: profile?.postal_address || ''
      });
      
      setBankingDetails({
        bank_name: profile?.bank_name || '',
        bank_branch_code: profile?.bank_branch_code || '',
        bank_account_number: profile?.bank_account_number || '',
        bank_account_type: profile?.bank_account_type || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSavePersonalInfo = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/profile/personal`, personalInfo, { headers });
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyPassword = async () => {
    setVerifyingPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/profile/banking?password=${encodeURIComponent(bankingPassword)}`, { headers });
      setBankingDetails(response.data);
      setShowFullBanking(true);
      setShowBankingDialog(false);
      setBankingPassword('');
      toast.success('Banking details revealed');
    } catch (error) {
      console.error('Error verifying password:', error);
      toast.error('Invalid password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleSaveBanking = async () => {
    if (!bankingPassword) {
      toast.error('Please enter your password to save banking details');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/profile/banking`, {
        current_password: bankingPassword,
        ...bankingDetails
      }, { headers });
      toast.success('Banking details updated successfully');
      setEditingBanking(false);
      setBankingPassword('');
      fetchProfile();
    } catch (error) {
      console.error('Error saving banking:', error);
      toast.error(error.response?.data?.detail || 'Failed to save banking details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="profile-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const completeness = profileData?.completeness || 0;

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            My Profile
          </h1>
          <p className="text-[#6C757D] mt-1">
            Manage your personal and business information
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchProfile}
          className="flex items-center gap-2"
          data-testid="refresh-profile-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Profile Completeness */}
      <Card data-testid="profile-completeness">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Profile Completeness</span>
            <span className="text-sm font-bold text-[#1D2D2A]">{completeness}%</span>
          </div>
          <Progress value={completeness} className="h-2 [&>div]:bg-[#1D2D2A]" />
          <div className="mt-2 flex flex-wrap gap-2">
            {profileData?.completeness_fields && Object.entries(profileData.completeness_fields).map(([field, completed]) => (
              <span 
                key={field}
                className={`text-xs px-2 py-1 rounded-full ${
                  completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {completed ? <CheckCircle2 className="h-3 w-3 inline mr-1" /> : <AlertCircle className="h-3 w-3 inline mr-1" />}
                {field.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card data-testid="personal-info-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-[#1D2D2A]" />
              Personal Information
            </CardTitle>
            <CardDescription>Your contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={personalInfo.first_name}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, first_name: e.target.value }))}
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={personalInfo.last_name}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, last_name: e.target.value }))}
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={profileData?.user?.email || ''}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500">Contact support to change your email address</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={personalInfo.phone}
                onChange={(e) => {
                  let v = e.target.value.trim();
                  // Prepend +27 if the user typed a local SA number without a country code.
                  if (v && !v.startsWith('+') && !v.startsWith('00')) {
                    if (v.startsWith('0')) v = '+27 ' + v.slice(1);
                    else if (/^[1-9]/.test(v)) v = '+27 ' + v;
                  }
                  setPersonalInfo(prev => ({ ...prev, phone: v }));
                }}
                placeholder="+27 83-703-8121"
                data-testid="input-phone"
              />
              <p className="text-xs text-gray-500">Include the international dialling code (e.g. +27 for South Africa).</p>
            </div>
          </CardContent>
        </Card>

        {/* Entity Details */}
        <Card data-testid="entity-details-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#1D2D2A]" />
              Entity Details
            </CardTitle>
            <CardDescription>Business or trust information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="entity_type">Entity Type</Label>
              <Select 
                value={personalInfo.entity_type} 
                onValueChange={(value) => setPersonalInfo(prev => ({ ...prev, entity_type: value }))}
              >
                <SelectTrigger data-testid="select-entity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company (Pty Ltd)</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="cc">Close Corporation (CC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {personalInfo.entity_type !== 'individual' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="entity_name">Entity Name</Label>
                  <Input
                    id="entity_name"
                    value={personalInfo.entity_name}
                    onChange={(e) => setPersonalInfo(prev => ({ ...prev, entity_name: e.target.value }))}
                    data-testid="input-entity-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Registration Number</Label>
                  <Input
                    id="registration_number"
                    value={personalInfo.registration_number}
                    onChange={(e) => setPersonalInfo(prev => ({ ...prev, registration_number: e.target.value }))}
                    placeholder="e.g., 2020/123456/07"
                    data-testid="input-registration-number"
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="tax_number">Tax Number (Income Tax Ref)</Label>
              <Input
                id="tax_number"
                type="text"
                value={personalInfo.tax_number}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, tax_number: e.target.value }))}
                placeholder="e.g. 0123/456/789 or 0123456789"
                data-testid="input-tax-number"
              />
              <p className="text-xs text-gray-500">Text field — slashes and spaces are allowed.</p>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card data-testid="address-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#1D2D2A]" />
              Address Information
            </CardTitle>
            <CardDescription>Physical and postal addresses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="physical_address">Physical Address</Label>
              <Input
                id="physical_address"
                value={personalInfo.physical_address}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, physical_address: e.target.value }))}
                placeholder="Street address, city, province, postal code"
                data-testid="input-physical-address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_address">Postal Address</Label>
              <Input
                id="postal_address"
                value={personalInfo.postal_address}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, postal_address: e.target.value }))}
                placeholder="PO Box or same as physical"
                data-testid="input-postal-address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Banking Details */}
        <Card data-testid="banking-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Landmark className="h-5 w-5 text-[#1D2D2A]" />
              Banking Details
              <Lock className="h-4 w-4 text-gray-400" />
            </CardTitle>
            <CardDescription>For receiving distributions (password protected)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showFullBanking && !editingBanking ? (
              <>
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input value={bankingDetails.bank_name || '••••••••'} disabled className="bg-gray-50" />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input 
                    value={profileData?.profile?.bank_account_number_masked || '••••••••'} 
                    disabled 
                    className="bg-gray-50" 
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowBankingDialog(true)}
                    className="flex items-center gap-2"
                    data-testid="view-banking-btn"
                  >
                    <Eye className="h-4 w-4" />
                    View Full Details
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setEditingBanking(true); setShowBankingDialog(true); }}
                    data-testid="edit-banking-btn"
                  >
                    Edit
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Select 
                    value={bankingDetails.bank_name} 
                    onValueChange={(value) => setBankingDetails(prev => ({ ...prev, bank_name: value }))}
                    disabled={!editingBanking}
                  >
                    <SelectTrigger data-testid="select-bank-name">
                      <SelectValue placeholder="Select bank" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ABSA">ABSA Bank</SelectItem>
                      <SelectItem value="FNB">First National Bank (FNB)</SelectItem>
                      <SelectItem value="Nedbank">Nedbank</SelectItem>
                      <SelectItem value="Standard Bank">Standard Bank</SelectItem>
                      <SelectItem value="Capitec">Capitec Bank</SelectItem>
                      <SelectItem value="Investec">Investec</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_branch_code">Branch Code</Label>
                  <Input
                    id="bank_branch_code"
                    value={bankingDetails.bank_branch_code}
                    onChange={(e) => setBankingDetails(prev => ({ ...prev, bank_branch_code: e.target.value }))}
                    disabled={!editingBanking}
                    className={!editingBanking ? 'bg-gray-50' : ''}
                    data-testid="input-branch-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input
                    id="bank_account_number"
                    value={bankingDetails.bank_account_number}
                    onChange={(e) => setBankingDetails(prev => ({ ...prev, bank_account_number: e.target.value }))}
                    disabled={!editingBanking}
                    className={!editingBanking ? 'bg-gray-50' : ''}
                    data-testid="input-account-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account_type">Account Type</Label>
                  <Select 
                    value={bankingDetails.bank_account_type} 
                    onValueChange={(value) => setBankingDetails(prev => ({ ...prev, bank_account_type: value }))}
                    disabled={!editingBanking}
                  >
                    <SelectTrigger data-testid="select-account-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cheque">Cheque/Current</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                      <SelectItem value="transmission">Transmission</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingBanking && (
                  <div className="space-y-2">
                    <Label htmlFor="banking_password">Confirm Password</Label>
                    <Input
                      id="banking_password"
                      type="password"
                      value={bankingPassword}
                      onChange={(e) => setBankingPassword(e.target.value)}
                      placeholder="Enter password to save changes"
                      data-testid="input-banking-password"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  {editingBanking ? (
                    <>
                      <Button onClick={handleSaveBanking} disabled={saving} data-testid="save-banking-btn">
                        <Save className="h-4 w-4 mr-2" />
                        Save Banking Details
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => { setEditingBanking(false); setShowFullBanking(false); setBankingPassword(''); }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setEditingBanking(true)} data-testid="edit-banking-details-btn">
                        Edit Details
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => { setShowFullBanking(false); }}
                      >
                        <EyeOff className="h-4 w-4 mr-2" />
                        Hide
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSavePersonalInfo} 
          disabled={saving}
          className="bg-[#1D2D2A] hover:bg-[#243D37]"
          data-testid="save-profile-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Password Verification Dialog */}
      <Dialog open={showBankingDialog} onOpenChange={setShowBankingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password Required</DialogTitle>
            <DialogDescription>
              Please enter your password to {editingBanking ? 'edit' : 'view'} your banking details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="verify_password">Password</Label>
              <Input
                id="verify_password"
                type="password"
                value={bankingPassword}
                onChange={(e) => setBankingPassword(e.target.value)}
                placeholder="Enter your password"
                data-testid="input-verify-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBankingDialog(false); setBankingPassword(''); setEditingBanking(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyPassword} 
              disabled={verifyingPassword || !bankingPassword}
              data-testid="verify-password-btn"
            >
              {verifyingPassword ? 'Verifying...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
